import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { motion } from "framer-motion";
import { ClipboardPaste, UploadCloud, FileText, ImageIcon, Video } from "lucide-react";
import { toast } from "sonner";

import { Textarea } from "@/components/ui/textarea";

const VIDEO_EXT = ["mp4", "mov", "webm"];
const IMAGE_EXT = ["jpg", "jpeg", "png", "webp"];
const TEXT_EXT = ["txt", "js", "ts", "jsx", "tsx", "py", "md", "json", "html", "css", "go", "rs", "java", "c", "cpp"];

const MAX_BYTES = 100 * 1024 * 1024;

function classify(file) {
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (VIDEO_EXT.includes(ext)) return "video";
    if (IMAGE_EXT.includes(ext)) return "image";
    if (TEXT_EXT.includes(ext)) return "text";
    return null;
}

export default function Uploader({ onFileAccepted, onTextPaste }) {
    const [mode, setMode] = useState("file"); // "file" | "paste"
    const [pasteValue, setPasteValue] = useState("");

    const handleFiles = useCallback(
        async (accepted) => {
            const file = accepted?.[0];
            if (!file) return;
            if (file.size > MAX_BYTES) {
                toast.error("File too large", {
                    description: "Max upload size is 100 MB.",
                });
                return;
            }
            const kind = classify(file);
            if (!kind) {
                toast.error("Unsupported file", {
                    description: "Use video (mp4/mov/webm), image (jpg/png/webp), or text/code.",
                });
                return;
            }
            let textContent = null;
            if (kind === "text") {
                try {
                    textContent = await file.text();
                } catch {
                    textContent = "";
                }
            }
            onFileAccepted(file, kind, textContent);
        },
        [onFileAccepted]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleFiles,
        multiple: false,
        accept: {
            "video/*": VIDEO_EXT.map((e) => `.${e}`),
            "image/*": IMAGE_EXT.map((e) => `.${e}`),
            "text/*": TEXT_EXT.map((e) => `.${e}`),
        },
        maxSize: MAX_BYTES,
    });

    const submitPaste = () => {
        const v = pasteValue.trim();
        if (!v) {
            toast.error("Paste something first");
            return;
        }
        onTextPaste(v);
    };

    return (
        <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
            className="flex flex-col gap-5"
        >
            <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.32em] text-[#8A93A6]">
                    upload
                </span>
                <div
                    className="inline-flex rounded-lg border border-[#1F2A40] bg-[#111726] p-1"
                    role="tablist"
                    data-testid="uploader-mode-toggle"
                >
                    <ModeButton
                        active={mode === "file"}
                        onClick={() => setMode("file")}
                        testId="mode-file-btn"
                    >
                        <UploadCloud className="mr-2 h-3.5 w-3.5" />
                        Drop file
                    </ModeButton>
                    <ModeButton
                        active={mode === "paste"}
                        onClick={() => setMode("paste")}
                        testId="mode-paste-btn"
                    >
                        <ClipboardPaste className="mr-2 h-3.5 w-3.5" />
                        Paste text
                    </ModeButton>
                </div>
            </div>

            {mode === "file" ? (
                <div
                    {...getRootProps()}
                    className={[
                        "group relative flex flex-col items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed p-14 text-center transition-all duration-300",
                        "bg-[#111726] text-[#E8ECF5]",
                        isDragActive
                            ? "border-[#00F5D4] bg-[#1A2235]/80 shadow-[0_0_32px_0_rgba(0,245,212,0.18)]"
                            : "border-[#1F2A40] hover:border-[#00F5D4]/70 hover:bg-[#1A2235]/40 hover:shadow-[0_0_24px_0_rgba(0,245,212,0.12)]",
                    ].join(" ")}
                    data-testid="upload-dropzone"
                >
                    <input
                        {...getInputProps()}
                        data-testid="upload-file-input"
                    />

                    <motion.div
                        animate={{
                            scale: isDragActive ? 1.08 : 1,
                            rotate: isDragActive ? -3 : 0,
                        }}
                        transition={{ type: "spring", stiffness: 300, damping: 22 }}
                        className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-[#1F2A40] bg-[#0B0F19] text-[#8A93A6] transition-colors duration-300 group-hover:border-[#00F5D4]/60 group-hover:text-[#00F5D4]"
                    >
                        <UploadCloud className="h-6 w-6" />
                    </motion.div>

                    <h3 className="text-lg font-medium tracking-tight text-[#E8ECF5]">
                        {isDragActive
                            ? "Drop it like it's already viral"
                            : "Drop a video, image, or code snippet"}
                    </h3>
                    <p className="mt-1 text-sm text-[#8A93A6]">
                        or click to browse — up to 100 MB
                    </p>

                    <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                        <Hint icon={<Video className="h-3 w-3" />}>mp4 · mov · webm</Hint>
                        <Hint icon={<ImageIcon className="h-3 w-3" />}>jpg · png · webp</Hint>
                        <Hint icon={<FileText className="h-3 w-3" />}>txt · md · code</Hint>
                    </div>
                </div>
            ) : (
                <div
                    className="flex flex-col gap-3 rounded-2xl border border-[#1F2A40] bg-[#111726] p-6"
                    data-testid="paste-text-panel"
                >
                    <Textarea
                        value={pasteValue}
                        onChange={(e) => setPasteValue(e.target.value)}
                        placeholder="Paste your raw text, idea, script, or code…"
                        className="min-h-[200px] resize-y border-[#1F2A40] bg-[#0B0F19] font-mono text-sm leading-relaxed text-[#E8ECF5] placeholder:text-[#8A93A6]/70 focus-visible:border-[#00F5D4] focus-visible:ring-[#00F5D4]/30"
                        data-testid="paste-text-input"
                    />
                    <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-[#8A93A6]">
                            {pasteValue.length.toLocaleString()} chars
                        </span>
                        <button
                            type="button"
                            onClick={submitPaste}
                            className="group relative overflow-hidden rounded-lg bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] px-5 py-2 text-sm font-medium text-white transition-all duration-300 hover:shadow-[0_0_20px_rgba(0,245,212,0.35)]"
                            data-testid="paste-submit-btn"
                        >
                            <span className="relative z-10">Analyze text</span>
                            <span
                                aria-hidden
                                className="absolute inset-0 bg-gradient-to-r from-transparent via-[#00F5D4]/30 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                            />
                        </button>
                    </div>
                </div>
            )}
        </motion.section>
    );
}

function ModeButton({ active, onClick, children, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={[
                "inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium transition-all",
                active
                    ? "bg-[#1A2235] text-[#00F5D4] shadow-[inset_0_0_0_1px_rgba(0,245,212,0.25)]"
                    : "text-[#8A93A6] hover:text-[#E8ECF5]",
            ].join(" ")}
        >
            {children}
        </button>
    );
}

function Hint({ icon, children }) {
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#1F2A40] bg-[#0B0F19] px-2.5 py-1 font-mono text-[10px] text-[#8A93A6]">
            {icon}
            {children}
        </span>
    );
}
